import React from 'react';
import { FaUsers, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import FollowerCard from './FollowerCard.jsx';
import LoadingSpinner from './LoadingSpinner.jsx';
import { useT } from '../../contexts/LocaleContext.jsx';
import '../../styles/partial/FollowersList.css';

const FollowersList = ({
  followers,
  isLoading,
  currentPage,
  totalPages,
  totalCount,
  perPage,
  onPageChange,
  onPromotionSuccess,
  bandId,
  hasActiveSearch,
}) => {
  const t = useT();

  if (isLoading) {
    return (
      <div className="flist__loading">
        <LoadingSpinner />
        <p>{t('loading.followers')}</p>
      </div>
    );
  }

  if (!followers || followers.length === 0) {
    return (
      <div className="flist__empty">
        <FaUsers className="flist__empty-icon" aria-hidden="true" />
        <p className="flist__empty-title">{t('followers.list.emptyTitle')}</p>
        <span className="flist__empty-hint">
          {hasActiveSearch
            ? t('followers.list.emptySearchHint')
            : t('followers.list.emptyHint')}
        </span>
      </div>
    );
  }

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const maxVisible = 5;
    const rawStart = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    const end = Math.min(totalPages, rawStart + maxVisible - 1);
    const start = Math.max(1, end - maxVisible + 1);
    const pages = [];

    pages.push(
      <button
        key="prev"
        className="flist__page-btn"
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        aria-label={t('followers.list.previousPage')}
      >
        <FaChevronLeft className="rtl-mirror" aria-hidden="true" />
      </button>
    );

    for (let i = start; i <= end; i++) {
      pages.push(
        <button
          key={i}
          className={`flist__page-btn${i === currentPage ? ' flist__page-btn--active' : ''}`}
          onClick={() => onPageChange(i)}
          aria-current={i === currentPage ? 'page' : undefined}
        >
          {i}
        </button>
      );
    }

    pages.push(
      <button
        key="next"
        className="flist__page-btn"
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        aria-label={t('followers.list.nextPage')}
      >
        <FaChevronRight className="rtl-mirror" aria-hidden="true" />
      </button>
    );

    const from = (currentPage - 1) * perPage + 1;
    const to = Math.min(currentPage * perPage, totalCount);

    return (
      <nav className="flist__pagination" aria-label={t('followers.list.paginationLabel')}>
        <span className="flist__pagination-info">
          {t('followers.list.pageRange', { from, to, total: totalCount })}
        </span>
        <div className="flist__pagination-controls">{pages}</div>
      </nav>
    );
  };

  return (
    <div className="flist">
      <div className="flist__grid">
        {followers.map((follower) => (
          <FollowerCard
            key={follower.user_id}
            follower={follower}
            onPromotionSuccess={onPromotionSuccess}
            bandId={bandId}
          />
        ))}
      </div>
      {renderPagination()}
    </div>
  );
};

export default FollowersList;
